import type {
  CatalogRecord,
  DemandGroupMutationResult,
  DemandRecord,
  DemandTargetAllocationGroup,
  DemandTargetAllocationGroupMutationResult,
  ReceivingAllocationGroup,
  RecordReceiptResult,
  RpcParams,
} from "@lodestone/hungry-lion-procurement-contracts";
import type { AuthSession } from "@lodestone/hungry-lion-procurement-client";
import { createContext, useContext, type ReactNode } from "react";

export type CatalogKind = CatalogRecord["kind"];
export type UnitOfMeasure = RpcParams<"catalog.skus.create">["unitOfMeasure"];

type ExistingRecord<TKind extends CatalogKind> = Extract<
  CatalogRecord,
  { kind: TKind }
>;

type SaveCatalogRecordCommon<TKind extends CatalogKind> = {
  existing?: ExistingRecord<TKind>;
  idempotencyKey: string;
  kind: TKind;
  name: string;
  projectID: string;
};

export type SaveCatalogRecordCommand =
  | (SaveCatalogRecordCommon<"Location"> & {
      locationType: "Store" | "DistributionCentre";
    })
  | (SaveCatalogRecordCommon<"SKU"> & {
      unitOfMeasure: UnitOfMeasure;
      categoryID?: string | null | undefined;
      erpNo?: string | null | undefined;
      description?: string | null | undefined;
    })
  | SaveCatalogRecordCommon<"Supplier">
  | SaveCatalogRecordCommon<"Category">;

export type CatalogScope = {
  kind: CatalogKind;
  locationType?: "Store" | "DistributionCentre";
  projectID: string;
};

export type CatalogRecordCommand = {
  idempotencyKey: string;
  projectID: string;
  record: CatalogRecord;
};

export type CatalogOperations = {
  list(scope: CatalogScope): Promise<CatalogRecord[]>;
  listUnitOfMeasures(projectID: string): Promise<UnitOfMeasure[]>;
  save(command: SaveCatalogRecordCommand): Promise<void>;
  setState(
    command: CatalogRecordCommand & { state: "Active" | "Retired" },
  ): Promise<void>;
  remove(command: CatalogRecordCommand): Promise<void>;
};

export type DemandOperations = {
  listAllocationGroups(
    projectID: string,
  ): Promise<DemandTargetAllocationGroup[]>;
  renameAllocationGroup(
    command: RpcParams<"demandTargetAllocationGroups.rename">,
  ): Promise<DemandTargetAllocationGroupMutationResult>;
  activateAllocationGroup(
    command: RpcParams<"demandTargetAllocationGroups.activate">,
  ): Promise<DemandTargetAllocationGroupMutationResult>;
  list(projectID: string): Promise<DemandRecord[]>;
  updateGroup(
    command: RpcParams<"demandGroups.update">,
  ): Promise<DemandGroupMutationResult>;
  renameGroup(
    command: RpcParams<"demandGroups.rename">,
  ): Promise<DemandGroupMutationResult>;
  createGroup(
    command: RpcParams<"demandGroups.create">,
  ): Promise<DemandGroupMutationResult>;
  activateGroup(
    command: RpcParams<"demandGroups.activate">,
  ): Promise<DemandGroupMutationResult>;
  assignTargets(
    command: RpcParams<"demandGroups.assignTargets">,
  ): Promise<DemandGroupMutationResult>;
};

export type ReceivingOperations = {
  list(
    projectID: string,
    locationID: string,
  ): Promise<ReceivingAllocationGroup[]>;
  record(command: RpcParams<"receiving.record">): Promise<RecordReceiptResult>;
};

export type ProcurementRuntime = {
  auth: {
    isAuthenticated: boolean;
    isLoading: boolean;
    session: AuthSession;
  };
  catalog: CatalogOperations;
  demands: DemandOperations;
  receiving: ReceivingOperations;
};

const ProcurementRuntimeContext = createContext<ProcurementRuntime | null>(
  null,
);

export const ProcurementRuntimeProvider = ({
  children,
  runtime,
}: {
  children: ReactNode;
  runtime: ProcurementRuntime;
}) => (
  <ProcurementRuntimeContext.Provider value={runtime}>
    {children}
  </ProcurementRuntimeContext.Provider>
);

export const useProcurementRuntime = () => {
  const runtime = useContext(ProcurementRuntimeContext);
  if (!runtime) {
    throw new Error(
      "useProcurementRuntime must be used within ProcurementRuntimeProvider",
    );
  }
  return runtime;
};
